import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, CircularProgress, IconButton, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { CashSaleService } from '../../services/cashSale.service';
import { SalesInvoiceService } from '../../services/salesInvoice.service';
import { CustomerService } from '../../services/customer.service';
import { SalesSettingsService } from '../../services/salesSettings.service';
import { AccountService } from '../../services/account.service';
import { SegmentService } from '../../services/segment.service';
import { AuthService } from '../../services/auth.service';
import { Customer } from '../../interfaces/sales/customer.interface';
import { SalesSettings } from '../../interfaces/sales/salesSettings.interface';
import { CashSaleSettings } from '../../interfaces/sales/cashSale.interface';
import { SalesInvoiceLineCreateDTO } from '../../interfaces/sales/salesInvoice.interface';
import { ReceiptMethod } from '../../interfaces/sales/receipt.interface';
import { Account, Segment } from '../../interfaces/accounting.interface';
import { TaxCodeLookup } from '../../interfaces/indirectTax.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import SearchableSelect from '../common/SearchableSelect';
import ScrollableTable from '../common/ScrollableTable';
import TaxCodeSelect from '../tax/TaxCodeSelect';
import SalesCatalogueSelect from './SalesCatalogueSelect';
import { hasTaxCodes, previewTax, useTaxCodeLookup } from '../tax/useTaxCodeLookup';
import { discountError, discountPatch, previewLine, shownDiscountAmount } from '../../utils/salesLineDiscount';
import { defaultReceiptMethodForAccount, cashSaleDepositOptions, leafAccounts } from '../../utils/postingAccounts';
import { formatMoney } from '../../utils/format';
import { toLocalIsoDate } from '../../utils/dateUtils';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

// FW-2B: a cash sale takes cash, bank or mobile money at the moment of sale. A cheque is a credit invoice with a cheque receipt.
const METHODS: { value: ReceiptMethod; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'MOBILE_BANKING', label: 'Mobile Banking' },
  { value: 'OTHER', label: 'Other' },
];

interface LineRow extends Omit<SalesInvoiceLineCreateDTO, 'quantity' | 'unitPrice' | 'taxRatePct'> {
  key: string;
  quantity: number;    // NaN while the field is empty
  unitPrice: number;
  taxRatePct: number;
  itemLabel?: string;
}

const defaultCode = (l: TaxCodeLookup | null) => {
  const id = l?.defaultOutputCodeId;
  return id ? l?.codes?.find(c => c.id === id && c.active) : undefined;
};

const netOf = (l: LineRow) => previewLine(l).net;   // RF-6: the one shared preview; the server recomputes
const taxOf = (l: LineRow) => {
  const rate = Number.isFinite(l.taxRatePct) ? l.taxRatePct : 0;
  return l.taxCodeId ? previewTax(netOf(l), rate) : +((netOf(l) * rate) / 100).toFixed(4);
};

const CashSaleForm: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const canSettings = usePermission(PERMISSIONS.SALES_SETTINGS_MANAGE);
  const institutionId = AuthService.getSelectedInstitution() ?? '';
  const keyCounter = useRef(0);
  const nextKey = () => String(++keyCounter.current);

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [cashSettings, setCashSettings] = useState<CashSaleSettings | null>(null);
  const [salesSettings, setSalesSettings] = useState<SalesSettings | null>(null);
  const [assetAccounts, setAssetAccounts] = useState<Account[]>([]);
  const [revenueAccounts, setRevenueAccounts] = useState<Account[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const taxLookup = useTaxCodeLookup(institutionId);
  const showTaxCodes = hasTaxCodes(taxLookup);

  const [customerId, setCustomerId] = useState('');
  const [walkInName, setWalkInName] = useState('');
  const [walkInPhone, setWalkInPhone] = useState('');
  const [issueDate, setIssueDate] = useState(toLocalIsoDate());
  const [method, setMethod] = useState<ReceiptMethod>('CASH');
  const [methodTouched, setMethodTouched] = useState(false);
  const [depositAccountId, setDepositAccountId] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [tendered, setTendered] = useState('');
  const [lines, setLines] = useState<LineRow[]>([]);
  const [bulkPct, setBulkPct] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const blankLine = (): LineRow => {
    const dc = defaultCode(taxLookup);
    return {
      key: nextKey(), description: '', quantity: 1, unitPrice: NaN,
      taxRatePct: dc ? dc.effectiveRatePct : (salesSettings?.defaultTaxRatePct ?? 0), taxCodeId: dc ? dc.id : null,
    };
  };

  useEffect(() => {
    Promise.all([
      CustomerService.listByInstitution(institutionId, 0, 200),
      CashSaleService.getSettings(institutionId).catch(() => null),
      SalesSettingsService.getByInstitution(institutionId).catch(() => null),
      AccountService.getAllAccounts(),
      SegmentService.getByInstitution(0, 200).catch(() => ({ content: [] as Segment[] })),
    ])
      .then(([customerPage, cs, ss, accounts, segPage]) => {
        setCustomers(customerPage.content);
        setCashSettings(cs);
        setSalesSettings(ss);
        const all: Account[] = Array.isArray(accounts) ? accounts : [];
        setAssetAccounts(all.filter(a => a.accountType === 'ASSET'));
        setRevenueAccounts(all.filter(a => a.accountType === 'REVENUE'));
        setSegments(((segPage as { content?: Segment[] }).content ?? []).filter(s => s.active !== false));
        if (cs?.depositAccountId) setDepositAccountId(cs.depositAccountId);
        if (cs?.receiptMethod) setMethod(cs.receiptMethod);
        setLines([{ key: nextKey(), description: '', quantity: 1, unitPrice: NaN, taxRatePct: ss?.defaultTaxRatePct ?? 0, taxCodeId: null }]);
      })
      .catch(e => toast.showApiError(e, 'Failed to load cash sale data'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // the tax lookup can arrive after the first blank line: give it the institution default code once
  const defaultsApplied = useRef(false);
  useEffect(() => {
    if (loading || !taxLookup || defaultsApplied.current) return;
    defaultsApplied.current = true;
    const dc = defaultCode(taxLookup);
    if (dc) setLines(prev => prev.map(l => (l.taxCodeId ? l : { ...l, taxCodeId: dc.id, taxRatePct: dc.effectiveRatePct })));
  }, [loading, taxLookup]);

  const setLine = (index: number, patch: Partial<LineRow>) =>
    setLines(prev => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  const subtotal = lines.reduce((s, l) => s + netOf(l), 0);
  const tax = lines.reduce((s, l) => s + taxOf(l), 0);
  const total = subtotal + tax;
  const tenderedNum = Number(tendered);
  const change = tendered !== '' && Number.isFinite(tenderedNum) ? tenderedNum - total : null;   // change is shown only, never stored

  const noWalkIn = !customerId && !cashSettings?.cashCustomerId;
  const depositMissing = !depositAccountId;
  const lineInvalid = (l: LineRow) => !(l.quantity > 0) || !(l.unitPrice > 0) || !!discountError(l);
  const submitDisabled = saving || noWalkIn || depositMissing || lines.length === 0 || lines.some(lineInvalid) || total <= 0;

  const save = async (print: boolean) => {
    setSaving(true);
    try {
      const result = await CashSaleService.create({
        institutionId,
        customerId: customerId || undefined,
        walkInName: walkInName.trim() || undefined,
        walkInPhone: walkInPhone.trim() || undefined,
        issueDate,
        method,
        depositAccountId,
        reference: reference.trim() || undefined,
        notes: notes.trim() || undefined,
        lines: lines.map(l => ({
          description: l.description,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          taxRatePct: Number.isFinite(l.taxRatePct) ? l.taxRatePct : 0,
          taxCodeId: l.taxCodeId || null,
          revenueAccountId: l.revenueAccountId || undefined,
          segmentId: l.segmentId || undefined,
          itemId: l.itemId || undefined,
          discountPct: l.discountPct ?? undefined,
          discountAmount: l.discountPct == null ? (l.discountAmount ?? undefined) : undefined,
        })),
      });
      toast.showSuccess(`Cash sale ${result.invoiceNumber} saved (receipt ${result.receiptNumber})`);
      if (print) {
        try {
          const blob = await SalesInvoiceService.downloadPdf(result.invoiceId);
          window.open(URL.createObjectURL(blob), '_blank');
        } catch (e) {
          toast.showApiError(e, 'The sale is saved, but the receipt PDF could not be opened');
        }
      }
      navigate(`/sales/invoices/${result.invoiceId}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to save cash sale');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  const needsSegment = (l: LineRow) =>
    revenueAccounts.find(a => a.id === (l.revenueAccountId || salesSettings?.defaultRevenueAccountId))?.segmentFieldPolicy === 'REQUIRED';
  const anySegment = lines.some(needsSegment);

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.cashSaleForm.page">
      <Typography variant="h5" sx={{ mb: 1 }}>Cash Sale</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        One step: the tax invoice is issued and the payment is received together. Nothing is e-mailed to the customer.
      </Typography>

      {noWalkIn && (
        <Paper sx={{ p: 2, mb: 2, borderLeft: 4, borderColor: 'warning.main' }}>
          <Typography variant="body2">
            No walk-in customer is configured. Pick a customer below
            {canSettings ? <>, or <Button data-help="sales.cashSaleForm.openSettings" size="small" onClick={() => navigate('/sales/settings')}>open Sales Settings</Button> to create the walk-in customer.</> : ' or ask an administrator to configure it.'}
          </Typography>
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 2 }} data-help-section="sales.cashSaleForm.section.header">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ flex: '1 1 240px', maxWidth: 360 }}>
            <SearchableSelect data-help="sales.cashSaleForm.field.customer"
              label="Customer (blank = walk-in)"
              options={customers}
              value={customerId}
              onChange={id => setCustomerId(id)}
              getOptionLabel={c => c.name}
              clearable
            />
          </Box>
          <TextField data-help="sales.cashSaleForm.field.buyerName" label="Buyer name (printed)" value={walkInName} onChange={e => setWalkInName(e.target.value)}
            sx={{ flex: '1 1 200px', maxWidth: 320 }} inputProps={{ maxLength: 255 }} />
          <TextField data-help="sales.cashSaleForm.field.buyerPhone" label="Buyer phone" value={walkInPhone} onChange={e => setWalkInPhone(e.target.value)}
            sx={{ flex: '1 1 150px', maxWidth: 220 }} inputProps={{ maxLength: 50 }} />
          <TextField data-help="sales.cashSaleForm.field.saleDate" label="Sale date" type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 160px', maxWidth: 220 }} required />
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
          <Box sx={{ flex: '1 1 260px', maxWidth: 400 }}>
            <AccountAutocomplete data-help="sales.cashSaleForm.field.depositAccount"
              label="Deposit account"
              accounts={cashSaleDepositOptions(assetAccounts)}
              value={depositAccountId}
              onChange={v => {
                setDepositAccountId(v);
                if (!methodTouched) setMethod(defaultReceiptMethodForAccount(assetAccounts.find(a => a.id === v)));
              }}
              placeholder="Choose an account"
            />
          </Box>
          <TextField data-help="sales.cashSaleForm.field.method" select label="Method" value={method}
            onChange={e => { setMethodTouched(true); setMethod(e.target.value as ReceiptMethod); }}
            sx={{ flex: '1 1 180px', maxWidth: 240 }}>
            {METHODS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
          </TextField>
          <TextField data-help="sales.cashSaleForm.field.reference" label="Reference" value={reference} onChange={e => setReference(e.target.value)}
            sx={{ flex: '1 1 180px', maxWidth: 260 }} inputProps={{ maxLength: 255 }} />
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }} data-help-section="sales.cashSaleForm.lines">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6">Items</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <TextField size="small" data-help="sales.cashSaleForm.bulkPct" label="Apply discount % to all lines" value={bulkPct}
              onChange={e => setBulkPct(e.target.value.replace(/[^0-9.]/g, ''))}
              inputProps={{ inputMode: 'decimal' }} sx={{ flex: '1 1 150px', maxWidth: 220 }} />
            <Button variant="outlined" data-help="sales.cashSaleForm.bulkApply" disabled={bulkPct === '' || Number(bulkPct) > 100}
              onClick={() => setLines(prev => prev.map(l => ({ ...l, discountPct: Number(bulkPct), discountAmount: null })))}>
              Apply
            </Button>
            <Button variant="outlined" data-help="sales.cashSaleForm.addLine" startIcon={<Add />} onClick={() => setLines(prev => [...prev, blankLine()])}>Add line</Button>
          </Box>
        </Box>
        <ScrollableTable>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell data-help="sales.cashSaleForm.col.product">Product / service</TableCell>
                <TableCell data-help="sales.cashSaleForm.col.description">Description</TableCell>
                <TableCell data-help="sales.cashSaleForm.col.qty">Qty</TableCell>
                <TableCell data-help="sales.cashSaleForm.col.unitPrice">Unit price</TableCell>
                <TableCell data-help="sales.cashSaleForm.col.discPct">Disc %</TableCell>
                <TableCell data-help="sales.cashSaleForm.col.discAmount">Disc amount</TableCell>
                {showTaxCodes && <TableCell data-help="sales.cashSaleForm.col.taxCode">Tax code</TableCell>}
                <TableCell data-help="sales.cashSaleForm.col.taxPct">Tax %</TableCell>
                <TableCell data-help="sales.cashSaleForm.col.revenue">Revenue account</TableCell>
                {anySegment && <TableCell data-help="sales.cashSaleForm.col.segment">Segment</TableCell>}
                <TableCell align="right" data-help="sales.cashSaleForm.col.net">Net</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line, index) => (
                <TableRow key={line.key}>
                  <TableCell sx={{ minWidth: 190 }}>
                    <SalesCatalogueSelect data-help="sales.cashSaleForm.col.product"
                      label="Product / service"
                      value={line.itemId ?? null}
                      valueLabel={line.itemLabel}
                      onChange={(id, item) => {
                        if (!id || !item) { setLine(index, { itemId: null, itemLabel: undefined }); return; }
                        const code = item.taxCodeId ? taxLookup?.codes?.find(c => c.id === item.taxCodeId && c.active) : undefined;
                        setLine(index, {
                          itemId: id, itemLabel: `${item.code} - ${item.name}`, description: item.name,
                          unitPrice: item.salePrice != null ? item.salePrice : line.unitPrice,
                          revenueAccountId: item.revenueAccountId || line.revenueAccountId,
                          ...(code ? { taxCodeId: code.id, taxRatePct: code.effectiveRatePct } : {}),
                        });
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField data-help="sales.cashSaleForm.col.description" size="small" value={line.description} placeholder="Description" sx={{ minWidth: 150 }}
                      onChange={e => setLine(index, { description: e.target.value })} />
                  </TableCell>
                  <TableCell>
                    <TextField data-help="sales.cashSaleForm.col.qty" type="number" size="small" value={Number.isFinite(line.quantity) ? line.quantity : ''}
                      onChange={e => setLine(index, { quantity: e.target.value === '' ? NaN : Number(e.target.value) })}
                      error={!(line.quantity > 0)} inputProps={{ 'aria-label': 'Quantity', step: 'any', min: 0 }} sx={{ width: 80 }} />
                  </TableCell>
                  <TableCell>
                    <TextField data-help="sales.cashSaleForm.col.unitPrice" type="number" size="small" value={Number.isFinite(line.unitPrice) ? line.unitPrice : ''}
                      onChange={e => setLine(index, { unitPrice: e.target.value === '' ? NaN : Number(e.target.value) })}
                      error={!(line.unitPrice > 0)} inputProps={{ 'aria-label': 'Unit price', step: 'any', min: 0 }} sx={{ width: 100 }} />
                  </TableCell>
                  <TableCell>
                    <TextField data-help="sales.cashSaleForm.col.discPct" type="number" size="small" inputProps={{ 'aria-label': 'Disc %', step: 'any', min: 0, max: 100 }}
                      value={line.discountPct ?? ''} error={!!discountError(line)} helperText={discountError(line) ?? undefined}
                      onChange={e => setLine(index, discountPatch('pct', e.target.value))} sx={{ width: 80 }} />
                  </TableCell>
                  <TableCell>
                    <TextField data-help="sales.cashSaleForm.col.discAmount" type="number" size="small" inputProps={{ 'aria-label': 'Disc amount', step: 'any', min: 0 }}
                      value={shownDiscountAmount(line)} onChange={e => setLine(index, discountPatch('amount', e.target.value))} sx={{ width: 100 }} />
                  </TableCell>
                  {showTaxCodes && (
                    <TableCell>
                      <TaxCodeSelect data-help="sales.cashSaleForm.col.taxCode" lookup={taxLookup} side="OUTPUT" value={line.taxCodeId}
                        onChange={(id, item) => setLine(index, {
                          taxCodeId: id, taxRatePct: item ? item.effectiveRatePct : (id ? line.taxRatePct : (salesSettings?.defaultTaxRatePct ?? 0)),
                        })} />
                    </TableCell>
                  )}
                  <TableCell>
                    <TextField data-help="sales.cashSaleForm.col.taxPct" type="number" size="small" disabled={!!line.taxCodeId} value={Number.isFinite(line.taxRatePct) ? line.taxRatePct : ''}
                      onChange={e => setLine(index, { taxRatePct: e.target.value === '' ? NaN : Number(e.target.value) })}
                      inputProps={{ 'aria-label': 'Tax %', step: 0.01, min: 0 }} sx={{ width: 80 }} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ minWidth: 170 }}>
                      <AccountAutocomplete data-help="sales.cashSaleForm.col.revenue" accounts={leafAccounts(revenueAccounts)} value={line.revenueAccountId ?? ''}
                        label="Revenue account" placeholder="Default"
                        onChange={id => setLine(index, { revenueAccountId: id || undefined })} />
                    </Box>
                  </TableCell>
                  {anySegment && (
                    <TableCell>
                      {needsSegment(line) && (
                        <SearchableSelect data-help="sales.cashSaleForm.col.segment" label="Segment" size="small" sx={{ minWidth: 150 }} options={segments}
                          value={line.segmentId ?? ''} onChange={v => setLine(index, { segmentId: v || undefined })}
                          getOptionLabel={s => `${s.code} - ${s.name}`} error={!line.segmentId} />
                      )}
                    </TableCell>
                  )}
                  <TableCell align="right">{formatMoney(netOf(line))}</TableCell>
                  <TableCell>
                    <IconButton size="small" color="error" data-help="sales.cashSaleForm.removeLine" aria-label="remove line" disabled={lines.length === 1}
                      onClick={() => setLines(prev => prev.filter((_, i) => i !== index))}><Delete /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollableTable>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }} data-help-section="sales.cashSaleForm.section.totals">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, flex: '1 1 320px' }}>
            <TextField data-help="sales.cashSaleForm.field.tendered" label="Amount tendered" type="number" value={tendered} onChange={e => setTendered(e.target.value)}
              inputProps={{ 'aria-label': 'Amount tendered', step: 'any', min: 0 }} sx={{ flex: '1 1 160px', maxWidth: 220 }} />
            <Box sx={{ alignSelf: 'center' }}>
              <Typography variant="body2" color="text.secondary">Change to give</Typography>
              <Typography variant="h6" data-testid="cash-change">{change == null ? '-' : formatMoney(Math.max(change, 0))}</Typography>
              {change != null && change < 0 && <Typography variant="caption" color="error">Short by {formatMoney(-change)}</Typography>}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 4 }}>
            <Box><Typography variant="body2" color="text.secondary">Subtotal</Typography><Typography>{formatMoney(subtotal)}</Typography></Box>
            <Box><Typography variant="body2" color="text.secondary">Tax</Typography><Typography>{formatMoney(tax)}</Typography></Box>
            <Box><Typography variant="body2" color="text.secondary">Total</Typography><Typography variant="h6" data-testid="cash-total">{formatMoney(total)}</Typography></Box>
          </Box>
        </Box>
        <TextField data-help="sales.cashSaleForm.field.notes" label="Notes" value={notes} onChange={e => setNotes(e.target.value)} multiline minRows={2}
          sx={{ mt: 2, flex: '1 1 300px', maxWidth: 560, display: 'flex' }} />
      </Paper>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Button variant="contained" data-help="sales.cashSaleForm.saveAndPrint" disabled={submitDisabled} onClick={() => save(true)}>
          {saving ? <CircularProgress size={20} /> : 'Save & print'}
        </Button>
        <Button variant="outlined" data-help="sales.cashSaleForm.save" disabled={submitDisabled} onClick={() => save(false)}>Save</Button>
        <Button variant="text" data-help="sales.cashSaleForm.cancel" disabled={saving} onClick={() => navigate(-1)}>Cancel</Button>
      </Box>
    </Box>
  );
};

export default CashSaleForm;
